import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  Box,
  Grid,
  Paper,
  CircularProgress,
  TextField,
  IconButton,
  Tabs,
  Tab,
  FormControl,
  MenuItem,
  Select,
  Alert as MuiAlert,
  InputAdornment,
  Snackbar,
  ButtonGroup
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PaymentIcon from '@mui/icons-material/Payment';
import { useAuth } from '../../../contexts/AuthContext';
import AddDebtModal from './AddDebtModal';
import {
  createAccrual,
  createPersonnelTransaction,
  getAllPersonnelTransactions,
} from '../../../services/migration/personnelTransactionService';
import ProjectSelector from '../../accounting/shared/ProjectSelector';
import { AppDatePicker } from '../../common/AppDatePicker';
import GuideButton from '../../common/guide/GuideButton';

// Tab paneli için interface
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

// Tab Paneli bileşeni
function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`modal-tabpanel-${index}`}
      aria-labelledby={`modal-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// Tab için yardımcı fonksiyon
function a11yProps(index: number) {
  return {
    id: `modal-tab-${index}`,
    'aria-controls': `modal-tabpanel-${index}`,
  };
}

// Modal prop'ları
interface SalaryBonusModalProps {
  open: boolean;
  onClose: () => void;
  personnelId: string;
  personnelName: string;
  companyId: string | null;
  onSuccess?: () => void;
  initialTab?: 'salary' | 'bonus'; // Başlangıçta hangi sekmenin açık olacağını belirten yeni prop
  /** Şube yöneticisi: ödeme yalnız bu şubenin kasa/banka hesaplarından yapılır, proje seçimi gizlenir. */
  restrictToBranchId?: string;
  hideProject?: boolean;
}

// Personel işlem verisi
interface PersonnelTransaction {
  id?: string | null;
  personnelId: string;
  personnelName: string;
  companyId: string | null;
  amount: number;
  description: string;
  paymentMethod: string;
  currency?: string;
  transactionType: string;
  cashboxId: string | null | undefined;
  cashboxName: string | null | undefined;
  bankAccountId: string | null | undefined;
  bankAccountName: string | null | undefined;
  performedBy: string;
  createdAt: string;
  isAccrual: boolean;
  type: string; // 'SALARY' veya 'BONUS'
}

// Para birimleri
const currencies = [
  { code: 'TRY', name: 'Türk Lirası', symbol: '₺' },
  { code: 'USD', name: 'Amerikan Doları', symbol: '$' },
  { code: 'EUR', name: 'Euro', symbol: '€' },
  { code: 'GBP', name: 'İngiliz Sterlini', symbol: '£' }
];

const SalaryBonusModal: React.FC<SalaryBonusModalProps> = ({
  open,
  onClose,
  personnelId,
  personnelName,
  companyId,
  onSuccess,
  initialTab = 'salary', // Varsayılan olarak maaş sekmesi
  restrictToBranchId,
  hideProject = false
}) => {
  const { userDetails } = useAuth();
  
  // Tab state'i - initialTab'e göre başlangıç değeri
  const [tabValue, setTabValue] = useState(initialTab === 'bonus' ? 1 : 0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form alanları
  const [transactionDate, setTransactionDate] = useState<string>(new Date().toISOString().split('T')[0]);
  const [currency, setCurrency] = useState<string>('TRY');
  const [amount, setAmount] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  // PYS: hakedişi bir projeye bağlar (opsiyonel)
  const [projectId, setProjectId] = useState<number | null>(null);

  // Snackbar state'leri
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  
  // Ödeme modalı için state
  const [openPaymentModal, setOpenPaymentModal] = useState(false);
  // "Oluştur ve Öde": önce hakediş oluşturulur, ödeme bu hakedişe bağlanır (kısmi ödeme desteklenir).
  const [payTarget, setPayTarget] = useState<{ id: string; amount: number; currency: string } | null>(null);
  const [payAfterCreate, setPayAfterCreate] = useState(false);

  // ✅ Avans uyarısı: maaş OLUŞTURURKEN (ödeme değil) son maaş hakedişinden bu yana
  // verilen avanslar tespit edilir ve yöneticiye "maaştan düşülsün mü?" diye sorulur.
  const [advanceWarning, setAdvanceWarning] = useState<{
    total: number;
    count: number;
    sinceLabel: string; // "son maaş hakedişinden (tarih) bu yana" | "bugüne kadar"
  } | null>(null);

  // Tab değişikliği
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    // Tab değiştiğinde açıklamayı doğrudan güncelle
    if (newValue === 0) {
      setDescription(`${personnelName} için maaş hakedişi`);
    } else {
      setDescription(`${personnelName} için prim hakedişi`);
    }
  };

  useEffect(() => {
    if (open) {
      // Modal açıldığında initialTab'e göre sekmeyi ayarla
      setTabValue(initialTab === 'bonus' ? 1 : 0);
      
      // Modal açıldığında form alanlarını sıfırla
      setTransactionDate(new Date().toISOString().split('T')[0]);
      setCurrency('TRY');
      setAmount('');
      setError(null);
      setSnackbarOpen(false);
      setAdvanceWarning(null);
      
      // Tab'a göre varsayılan açıklama
      const tabVal = initialTab === 'bonus' ? 1 : 0;
      setDescription(tabVal === 0 
        ? `${personnelName} için maaş hakedişi` 
        : `${personnelName} için prim hakedişi`);
    }
  }, [open, personnelName, initialTab]);

  // Para birimini formatla
  const formatCurrency = (num: number, currencyCode: string = 'TRY') => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: currencyCode,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  // Para birimi sembolünü bul
  const getCurrencySymbol = (currencyCode: string): string => {
    const found = currencies.find(c => c.code === currencyCode);
    return found ? found.symbol : currencyCode;
  };

  const getUserFullName = (): string => {
    if (userDetails && userDetails.firstName && userDetails.lastName) {
      return `${userDetails.firstName} ${userDetails.lastName}`;
    } else if (userDetails && userDetails.firstName) {
      return userDetails.firstName;
    } else if (userDetails && userDetails.email) {
      return userDetails.email;
    } else {
      return 'Sistem';
    }
  };

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbarOpen(false);
  };

  /**
   * Son maaş hakedişinden (yoksa tüm geçmişten) bu yana verilen avansları bulur.
   * Avans tespiti: kind='ADVANCE' işaretli ödemeler; eski kayıtlar için
   * açıklamasında "avans" geçen, hakedişe bağlı olmayan PAYMENT'lar (fallback).
   */
  const findAdvancesSinceLastSalary = async (): Promise<{ total: number; count: number; sinceLabel: string }> => {
    // Tüm geçmiş (sayfa sınırı yok): eski avanslar ya da son maaş kaçmasın.
    const data = await getAllPersonnelTransactions(personnelId);

    const lastSalary = data
      .filter((t) => t.isAccrual && t.kind === 'SALARY')
      .sort((a, b) => new Date(b.createdAt || 0).getTime() - new Date(a.createdAt || 0).getTime())[0];
    const sinceTs = lastSalary ? new Date(lastSalary.createdAt || 0).getTime() : 0;

    const advances = data.filter((t) => {
      if (t.transactionType !== 'PAYMENT' || t.isAccrual || t.accrualId) return false;
      if ((t.currency || 'TRY') !== currency) return false;
      if (new Date(t.createdAt || 0).getTime() <= sinceTs) return false;
      const isMarked = t.kind === 'ADVANCE';
      const looksLikeAdvance = /avans|advance/i.test(t.description || '');
      return isMarked || looksLikeAdvance;
    });

    const total = advances.reduce((sum, t) => sum + t.amount, 0);
    const sinceLabel = lastSalary
      ? `son maaş hakedişinden (${new Date(lastSalary.createdAt).toLocaleDateString('tr-TR')}) bu yana`
      : 'bugüne kadar';
    return { total, count: advances.length, sinceLabel };
  };

  /**
   * Hakedişi oluşturur. deductAdvance > 0 ise maaş tutarından düşer ve düşülen
   * tutar için "avans mahsubu" (tahsilat, para hareketi yok) kaydı ekler —
   * böylece defterdeki avans alacağı kapanır ve bakiye tutarlı kalır.
   */
  const doCreateAccrual = async (deductAdvance: number = 0, pay: boolean = false) => {
    setLoading(true);
    setError(null);
    const amountValue = parseFloat(amount);
    const finalAmount = amountValue - deductAdvance;

    try {
      // SQL: maaş/prim hakedişi (accrual RECEIVABLE — para hareketi yok)
      const created = await createAccrual({
        personnelFirebaseUid: personnelId,
        personnelName,
        amount: finalAmount,
        currency,
        description: deductAdvance > 0
          ? `${description} (${formatCurrency(deductAdvance, currency)} avans düşüldü)`
          : description,
        kind: tabValue === 0 ? 'SALARY' : 'BONUS',
        transactionDate: transactionDate ? new Date(transactionDate).toISOString() : undefined,
        performedBy: getUserFullName(),
        projectId,
      });

      if (deductAdvance > 0) {
        // Avans mahsubu: verilen avans maaştan düşüldüğü için avans alacağı
        // tahsilatla (para hareketi olmadan) kapatılır.
        await createPersonnelTransaction({
          personnelFirebaseUid: personnelId,
          personnelName,
          transactionType: 'COLLECTION',
          amount: deductAdvance,
          currency,
          description: `Avans mahsubu — maaştan düşüldü (${personnelName})`,
          paymentMethod: 'accrual',
          transactionDate: transactionDate ? new Date(transactionDate).toISOString() : undefined,
          performedBy: getUserFullName(),
        });
      }

      if (pay) {
        // Hakediş oluştu; ödeme penceresi bu hakedişe bağlı açılır.
        setPayTarget({ id: created.id, amount: finalAmount, currency });
        setOpenPaymentModal(true);
        return;
      }

      setSnackbarMessage(
        tabValue === 0
          ? (deductAdvance > 0
              ? `Maaş alacağı kaydedildi (${formatCurrency(deductAdvance, currency)} avans düşüldü)!`
              : 'Maaş alacağı başarıyla kaydedildi!')
          : 'Prim alacağı başarıyla kaydedildi!'
      );
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      
      if(onSuccess) onSuccess();
      setTimeout(() => {
        onClose();
      }, 2000);
    } catch (err: any) {
      const msg = err?.response?.data?.message || err.message;
      console.error("Kayıt sırasında hata:", err);
      setError("Kayıt sırasında bir hata oluştu: " + msg);
      setSnackbarMessage("Kayıt sırasında bir hata oluştu: " + msg);
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (pay: boolean = false) => {
    setError(null);
    setPayAfterCreate(pay);
    const amountValue = parseFloat(amount);

    if (!amount || amountValue <= 0) {
      setError("Lütfen geçerli bir tutar girin.");
      setSnackbarMessage("Lütfen geçerli bir tutar girin.");
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    if (!companyId || !personnelId) {
      setError("Gerekli alanlar eksik. Lütfen yöneticinize başvurun.");
      setSnackbarMessage("Gerekli alanlar eksik. Lütfen yöneticinize başvurun.");
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    // ✅ SADECE MAAŞ OLUŞTURURKEN: son maaştan bu yana verilen avansları kontrol et.
    // Avans varsa yöneticiye sorulur; oluşturma işlemi onaydan sonra devam eder.
    if (tabValue === 0) {
      setLoading(true);
      try {
        const result = await findAdvancesSinceLastSalary();
        if (result.total > 0) {
          setLoading(false);
          setAdvanceWarning(result);
          return; // Onay diyaloğu üzerinden devam edilecek
        }
      } catch (err) {
        // Avans kontrolü başarısız olsa bile maaş oluşturma engellenmez — sadece logla.
        console.warn('Avans kontrolü yapılamadı:', err);
      }
      setLoading(false);
    }

    await doCreateAccrual(0, pay);
  };

  // "Oluştur ve Öde": hakediş oluşturulur, ardından ona bağlı ödeme penceresi açılır
  const handleMakePayment = () => handleSubmit(true);

  // Ödeme modalı kapandığında: hakediş oluşmuş olduğu için liste yenilenir ve pencere kapanır
  // (ödeme yapılmadan kapatıldıysa hakediş "Ödenmedi" olarak kalır, Maaş/Prim Ödemesi'nden ödenebilir).
  const handlePaymentModalClose = () => {
    setOpenPaymentModal(false);
    setPayTarget(null);
    if (onSuccess) onSuccess();
    onClose();
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={loading ? undefined : onClose}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">
              {tabValue === 0 ? 'Maaş Oluştur' : 'Prim Oluştur'}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <GuideButton guideKey="personnel.salaryBonus" color="inherit" disabled={loading} />
              <IconButton edge="end" color="inherit" onClick={onClose} disabled={loading}>
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>
        </DialogTitle>
        
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="modal tabs" centered>
            <Tab label="Maaş" {...a11yProps(0)} />
            <Tab label="Prim" {...a11yProps(1)} />
          </Tabs>
        </Box>
        
        <DialogContent dividers>
          {error && (
            <MuiAlert severity="error" sx={{ mb: 2 }}>
              {error}
            </MuiAlert>
          )}
          
          <Box p={1}>
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography variant="subtitle1" gutterBottom>
                Personel Bilgisi
              </Typography>
              <Typography variant="h6">{personnelName}</Typography>
              <Typography variant="body2" color="text.secondary">
                ID: {personnelId}
              </Typography>
            </Paper>
          
            <TabPanel value={tabValue} index={0}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <AppDatePicker
                    label="İşlem Tarihi"
                    value={transactionDate}
                    onChange={setTransactionDate}
                    fullWidth
                    size="medium"
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <Select
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value)}
                      size="small"
                    >
                      {currencies.map((curr) => (
                        <MenuItem key={curr.code} value={curr.code}>
                          {curr.name} ({curr.symbol})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Maaş Tutarı"
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    fullWidth
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {getCurrencySymbol(currency)}
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Açıklama"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    fullWidth
                    multiline
                    rows={3}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                {/* Proje bağı (PYS) */}
                {!hideProject && (
                  <Grid item xs={12} md={6}>
                    <ProjectSelector value={projectId} onChange={setProjectId} disabled={loading} size="medium" />
                  </Grid>
                )}
              </Grid>
            </TabPanel>
            
            <TabPanel value={tabValue} index={1}>
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <AppDatePicker
                    label="İşlem Tarihi"
                    value={transactionDate}
                    onChange={setTransactionDate}
                    fullWidth
                    size="medium"
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth sx={{ mb: 2 }}>
                    <Select
                      value={currency}
                      onChange={(e) => setCurrency(e.target.value)}
                      size="small"
                    >
                      {currencies.map((curr) => (
                        <MenuItem key={curr.code} value={curr.code}>
                          {curr.name} ({curr.symbol})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <TextField
                    label="Prim Tutarı"
                    type="number"
                    value={amount}
                    onChange={(e) => setAmount(e.target.value)}
                    fullWidth
                    required
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          {getCurrencySymbol(currency)}
                        </InputAdornment>
                      ),
                    }}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Açıklama"
                    value={description}
                    onChange={(e) => setDescription(e.target.value)}
                    fullWidth
                    multiline
                    rows={3}
                    sx={{ mb: 2 }}
                  />
                </Grid>
                {/* Proje bağı (PYS) */}
                {!hideProject && (
                  <Grid item xs={12} md={6}>
                    <ProjectSelector value={projectId} onChange={setProjectId} disabled={loading} size="medium" />
                  </Grid>
                )}
              </Grid>
            </TabPanel>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button
            onClick={onClose}
            disabled={loading}
            variant="outlined"
          >
            İptal
          </Button>
          <ButtonGroup>
            {parseFloat(amount) > 0 && (
              <Button
                variant="outlined"
                color="primary"
                startIcon={<PaymentIcon />}
                onClick={handleMakePayment}
                disabled={loading || !amount || parseFloat(amount) <= 0}
              >
                Oluştur ve Öde
              </Button>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleSubmit(false)}
              disabled={loading || !amount || parseFloat(amount) <= 0}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 
                (tabValue === 0 ? 'Maaş Oluştur' : 'Prim Oluştur')}
            </Button>
          </ButtonGroup>
        </DialogActions>

        <Snackbar 
          open={snackbarOpen} 
          autoHideDuration={3000} 
          onClose={handleSnackbarClose}
          anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        >
          <MuiAlert onClose={handleSnackbarClose} severity={snackbarSeverity} sx={{ width: '100%' }} variant="filled">
            {snackbarMessage}
          </MuiAlert>
        </Snackbar>
      </Dialog>

      {/* ✅ Avans uyarı diyaloğu — maaş OLUŞTURURKEN gösterilir */}
      <Dialog
        open={!!advanceWarning}
        onClose={loading ? undefined : () => setAdvanceWarning(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Avans Uyarısı</DialogTitle>
        <DialogContent dividers>
          <MuiAlert severity="warning" sx={{ mb: 2 }}>
            <strong>{personnelName}</strong>, {advanceWarning?.sinceLabel}{' '}
            <strong>{advanceWarning ? formatCurrency(advanceWarning.total, currency) : ''}</strong> avans aldı
            ({advanceWarning?.count} işlem).
          </MuiAlert>
          <Typography variant="body2" sx={{ mb: 1 }}>
            Avans maaştan düşülsün mü?
          </Typography>
          {advanceWarning && parseFloat(amount) > advanceWarning.total ? (
            <Typography variant="body2" color="text.secondary">
              Düşülürse maaş hakedişi{' '}
              <strong>{formatCurrency(parseFloat(amount) - advanceWarning.total, currency)}</strong> olarak
              oluşturulur ve avans mahsup kaydı eklenir.
            </Typography>
          ) : (
            <MuiAlert severity="error" sx={{ mt: 1 }}>
              Avans toplamı maaş tutarına eşit veya daha büyük olduğu için otomatik düşüm yapılamaz.
              Maaş tam tutarla oluşturulabilir; mahsubu manuel yapabilirsiniz.
            </MuiAlert>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setAdvanceWarning(null)} disabled={loading}>
            İptal
          </Button>
          <Button
            variant="outlined"
            color="primary"
            disabled={loading}
            onClick={async () => {
              setAdvanceWarning(null);
              await doCreateAccrual(0, payAfterCreate);
            }}
          >
            Düşme, Tam Tutar Oluştur
          </Button>
          <Button
            variant="contained"
            color="warning"
            disabled={loading || !advanceWarning || parseFloat(amount) <= advanceWarning.total}
            onClick={async () => {
              const deduct = advanceWarning?.total || 0;
              setAdvanceWarning(null);
              await doCreateAccrual(deduct, payAfterCreate);
            }}
          >
            {loading ? <CircularProgress size={22} color="inherit" /> : 'Evet, Maaştan Düş'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Ödeme yapma modalı */}
      {openPaymentModal && payTarget && (
        <AddDebtModal
          open={openPaymentModal}
          onClose={handlePaymentModalClose}
          personnelId={personnelId}
          personnelName={personnelName}
          companyId={companyId}
          sourceAction="payment_collection"
          onSuccess={handlePaymentModalClose}
          initialPaymentDescription={`${personnelName} için ${tabValue === 0 ? 'maaş' : 'prim'} ödemesi`}
          initialPaymentAmount={String(payTarget.amount)}
          accrualId={payTarget.id}
          accrualCurrency={payTarget.currency}
          accrualRemaining={payTarget.amount}
          restrictToBranchId={restrictToBranchId}
          hideProject={hideProject}
        />
      )}
    </>
  );
};

export default SalaryBonusModal; 